import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    BookOpen, Lightbulb, Target, GraduationCap, Brain, FileText,
    CornerUpRight, MessagesSquare, Layers, Trash2, StickyNote, Save,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { termsApi, flashcardsApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";

const Field = ({ icon: Icon, label, accent, children }) => (children ? (
    <div className="flex gap-2.5">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md" style={{ background: `${accent}22`, color: accent }}>
            <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
            <div className="mono-label text-t4">{label}</div>
            <p className="mt-0.5 text-sm leading-relaxed text-t2">{children}</p>
        </div>
    </div>
) : null);

const STATUSES = ["new", "review", "understood"];

export const GlossaryDetail = ({ item, onClose, onChange }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [local, setLocal] = useState(item);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState("");

    useEffect(() => { setLocal(item); setNote(item?.note || ""); }, [item]);
    if (!item) return null;
    const it = local || item;
    const ex = it.explanation || {};

    const setStatus = async (status) => {
        setBusy("status");
        try { await termsApi.updateItem(it.id, { status }); setLocal((p) => ({ ...p, status })); toast.success(t("glossary.statusUpdated")); onChange?.({ ...it, status }); }
        catch (e) { toast.error(t("glossary.error")); } finally { setBusy(""); }
    };
    const saveNote = async () => {
        setBusy("note");
        try { await termsApi.updateItem(it.id, { note }); setLocal((p) => ({ ...p, note })); toast.success(t("glossary.noteSaved")); onChange?.({ ...it, note }); }
        catch (e) { toast.error(t("glossary.error")); } finally { setBusy(""); }
    };
    const addFlashcard = async () => {
        setBusy("card");
        try {
            await flashcardsApi.add(it.document_id, { front: it.term, back: ex.definition || it.definition || "", hint: ex.memory_tip || undefined, topic: "Glossary", type: "basic" });
            setLocal((p) => ({ ...p, in_flashcards: true })); toast.success(t("glossary.addedFlashcard")); onChange?.({ ...it, in_flashcards: true });
        } catch (e) { toast.error(t("glossary.error")); } finally { setBusy(""); }
    };
    const remove = async () => {
        setBusy("remove");
        try { await termsApi.removeItem(it.id); toast.success(t("glossary.removed")); onChange?.(null, it.id); onClose?.(); }
        catch (e) { toast.error(t("glossary.error")); } finally { setBusy(""); }
    };
    const openDoc = () => navigate(`/read/${it.document_id}`);
    const jump = () => navigate(`/read/${it.document_id}${it.word_start != null ? `?w=${it.word_start}` : ""}`);
    const ask = () => navigate(`/read/${it.document_id}?ask=${encodeURIComponent(it.term)}`);

    return (
        <Sheet open={!!item} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <SheetContent side="right" className="w-full border-border bg-surface-1 p-0 sm:max-w-lg" data-testid="glossary-detail">
                <div className="flex h-full flex-col">
                    <div className="border-b border-border px-5 py-4">
                        <div className="flex items-center gap-2">
                            {it.domain && it.domain !== "general" && (
                                <Badge className="bg-surface-3 text-[10px] uppercase" data-testid="glossary-detail-domain">{t(`glossary.domain.${it.domain}`)}</Badge>
                            )}
                            {it.revisits > 0 && <span className="mono-label text-t4">{t("glossary.revisitsLabel", { n: it.revisits })}</span>}
                        </div>
                        <h2 className="mt-1 font-sans-display text-xl font-semibold text-t1" data-testid="glossary-detail-term">{it.term}</h2>
                        <div className="mt-3 flex w-fit items-center gap-0.5 rounded-lg bg-surface-2 p-0.5" data-testid="glossary-status-switch">
                            {STATUSES.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`glossary-status-${s}`}
                                    onClick={() => setStatus(s)}
                                    disabled={busy === "status"}
                                    className={cn("rounded-md px-2.5 py-1 text-xs transition-colors", it.status === s ? "bg-surface-3 text-t1 shadow-elev-1" : "text-t3 hover:text-t1")}
                                >
                                    {t(`glossary.status.${s}`)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                        <Field icon={BookOpen} label={t("glossary.fields.definition")} accent="hsl(174 72% 46%)">{ex.definition || it.definition}</Field>
                        <Field icon={Lightbulb} label={t("glossary.fields.simply")} accent="hsl(38 82% 58%)">{ex.eli_new}</Field>
                        <Field icon={Target} label={t("glossary.fields.whyMatters")} accent="hsl(206 70% 55%)">{ex.why_matters}</Field>
                        <Field icon={GraduationCap} label={t("glossary.fields.examRelevance")} accent="hsl(330 62% 64%)">{ex.exam_relevance}</Field>
                        <Field icon={Brain} label={t("glossary.fields.memoryTip")} accent="hsl(150 52% 52%)">{ex.memory_tip}</Field>
                        {ex.related_concepts?.length > 0 && (
                            <div>
                                <div className="mono-label mb-1.5 text-t4">{t("glossary.fields.related")}</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {ex.related_concepts.map((c, i) => <span key={i} className="rounded-full border border-border bg-surface-2 px-2.5 py-1 text-xs text-t2">{c}</span>)}
                                </div>
                            </div>
                        )}

                        <div className="rounded-xl border border-border bg-surface-2/50 p-3" data-testid="glossary-source">
                            <div className="mono-label mb-1 text-t4">{t("glossary.fields.source")}</div>
                            <div className="flex items-center gap-2 text-sm text-t2"><FileText className="h-3.5 w-3.5 shrink-0 text-t4" />{it.document_title || "—"}</div>
                            {it.passage && <p className="mt-2 text-xs italic leading-relaxed text-t3">“{it.passage.slice(0, 240)}{it.passage.length > 240 ? "…" : ""}”</p>}
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button data-testid="glossary-jump-button" size="sm" variant="secondary" className="gap-1.5 bg-surface-3/60" onClick={jump}><CornerUpRight className="h-3.5 w-3.5" />{t("glossary.actions.jump")}</Button>
                                <Button data-testid="glossary-opendoc-button" size="sm" variant="ghost" className="text-t3" onClick={openDoc}>{t("glossary.actions.openDoc")}</Button>
                            </div>
                        </div>

                        <div>
                            <div className="mono-label mb-1.5 flex items-center gap-1.5 text-t4"><StickyNote className="h-3.5 w-3.5" />{t("glossary.actions.note")}</div>
                            <Textarea data-testid="glossary-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("glossary.actions.notePlaceholder")} className="min-h-[70px] resize-none bg-surface-2 text-sm" />
                            <div className="mt-2 flex justify-end">
                                <Button data-testid="glossary-note-save" size="sm" className="gap-1.5" disabled={busy === "note"} onClick={saveNote}><Save className="h-3.5 w-3.5" />{t("glossary.actions.saveNote")}</Button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 border-t border-border p-3">
                        <Button data-testid="glossary-flashcard-button" variant="secondary" size="sm" className="gap-1.5 bg-surface-3/60" disabled={busy === "card" || it.in_flashcards} onClick={addFlashcard}>
                            <Layers className="h-4 w-4" />{it.in_flashcards ? t("glossary.actions.added") : t("glossary.actions.addFlashcard")}
                        </Button>
                        <Button data-testid="glossary-ask-button" variant="secondary" size="sm" className="gap-1.5 bg-surface-3/60" onClick={ask}><MessagesSquare className="h-4 w-4" />{t("glossary.actions.ask")}</Button>
                        <Button data-testid="glossary-remove-button" variant="ghost" size="sm" className="gap-1.5 text-[hsl(var(--destructive))]" disabled={busy === "remove"} onClick={remove}><Trash2 className="h-4 w-4" />{t("glossary.actions.remove")}</Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
